/**
 * Bundle Selector - Thermostaat selectie bij vloerverwarming
 * Versie 2.0 - Volledig herzien voor betere variant tracking
 */

class BundleSelector {
  constructor(selector) {
    this.selector = selector;
    this.thermostaatInput = document.getElementById('bundle-thermostaat-id');
    this.quantityInput = document.querySelector('input[name="quantity"]');
    
    // Store variant data per product
    this.variantData = {};
    
    // Track selected variant per product (key = productId, value = variantId)
    this.selectedVariants = {};
    
    // Prevent double submissions
    this.isSubmitting = false;

    this.init();
  }

  init() {
    this.parseVariantData();
    this.initializeSelectedVariants();
    this.bindEvents();
    this.updateTotal();
    
    // Add styling class to submit button
    const submitButton = document.querySelector('button[type="submit"][name="add"], .product-form__submit');
    if (submitButton) {
      submitButton.classList.add('bundle-styled');
    }
    
    console.log('Bundle selector initialized', {
      variantData: this.variantData,
      selectedVariants: this.selectedVariants
    });
  }

  parseVariantData() {
    // Parse all variant JSON data
    const variantJsonElements = this.selector.querySelectorAll('[data-variants-json]');
    variantJsonElements.forEach(el => {
      const container = el.closest('[data-variant-selector]');
      if (container) {
        const productId = container.dataset.productId;
        try {
          this.variantData[productId] = JSON.parse(el.textContent);
        } catch (e) {
          console.error('Failed to parse variant data for product', productId, e);
        }
      }
    });
  }

  initializeSelectedVariants() {
    // Initialize selected variants based on initial radio button values
    this.selector.querySelectorAll('input[name="bundle-thermostaat"]').forEach(radio => {
      const productId = radio.dataset.productId;
      const variantId = radio.dataset.variantId;
      if (productId && variantId) {
        this.selectedVariants[productId] = parseInt(variantId);
      }
    });
  }

  bindEvents() {
    // Product selection (radio buttons)
    this.selector.querySelectorAll('input[name="bundle-thermostaat"]').forEach(radio => {
      radio.addEventListener('change', (e) => {
        const option = e.target.closest('[data-bundle-option]');
        this.onProductChange(option);
      });
    });

    // Variant option clicks (swatches and buttons)
    this.selector.querySelectorAll('.bundle-swatch, .bundle-variant-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.onVariantOptionClick(e, btn));
    });

    // Variant select changes (dropdowns)
    this.selector.querySelectorAll('.bundle-variant-select').forEach(select => {
      select.addEventListener('change', (e) => this.onVariantSelectChange(e, select));
    });

    // Intercept form submission
    this.interceptFormSubmit();

    // Listen for main product variant changes
    document.addEventListener('variant:changed', (e) => {
      this.updateTotal();
    });
  }

  onProductChange(option) {
    if (!option) return;
    
    const input = option.querySelector('input[type="radio"]');
    const productId = input?.dataset.productId;
    
    // Get the currently selected variant for this product
    const currentVariantId = this.selectedVariants[productId];
    
    // Update hidden input with the correct variant
    if (this.thermostaatInput && currentVariantId) {
      this.thermostaatInput.value = currentVariantId;
    } else if (this.thermostaatInput && input) {
      this.thermostaatInput.value = input.dataset.variantId || '';
    }

    // Update total
    this.updateTotal();
    
    console.log('Product changed:', {
      productId,
      selectedVariantId: currentVariantId || input?.dataset.variantId
    });
  }

  onVariantOptionClick(e, btn) {
    e.preventDefault();
    e.stopPropagation();

    const variantSelector = btn.closest('[data-variant-selector]');
    const bundleOption = btn.closest('[data-bundle-option]');
    const productId = variantSelector?.dataset.productId;
    
    if (!productId || !this.variantData[productId]) {
      console.warn('No variant data for product', productId);
      return;
    }

    // Update active state within this option group
    const group = btn.closest('.bundle-variant-swatches, .bundle-variant-buttons');
    if (group) {
      group.querySelectorAll('.bundle-swatch, .bundle-variant-btn').forEach(b => {
        b.classList.remove('is-active');
      });
      btn.classList.add('is-active');
    }

    // Update selected label (for color swatches)
    const optionName = btn.dataset.optionName;
    const selectedLabel = variantSelector.querySelector(`[data-selected-label="${optionName}"]`);
    if (selectedLabel) {
      selectedLabel.textContent = btn.dataset.value;
    }

    // Find matching variant
    const selectedOptions = this.getSelectedOptions(variantSelector);
    const matchingVariant = this.findVariant(productId, selectedOptions);

    console.log('Variant option clicked:', {
      productId,
      optionName,
      value: btn.dataset.value,
      selectedOptions,
      matchingVariant
    });

    if (matchingVariant) {
      // Store the selected variant for this product
      this.selectedVariants[productId] = matchingVariant.id;
      
      // Update the display
      this.updateVariantDisplay(bundleOption, matchingVariant);
      
      // Also select this product if not already selected
      const radioInput = bundleOption.querySelector('input[type="radio"]');
      if (radioInput) {
        if (!radioInput.checked) {
          radioInput.checked = true;
        }
        // Always update the radio's data attributes
        radioInput.dataset.variantId = matchingVariant.id;
        radioInput.value = matchingVariant.id;
        radioInput.dataset.price = matchingVariant.price;
        
        this.onProductChange(bundleOption);
      }
    }
  }

  onVariantSelectChange(e, select) {
    const variantSelector = select.closest('[data-variant-selector]');
    const bundleOption = select.closest('[data-bundle-option]');
    const productId = variantSelector?.dataset.productId;
    
    if (!productId || !this.variantData[productId]) return;

    const selectedOptions = this.getSelectedOptions(variantSelector);
    const matchingVariant = this.findVariant(productId, selectedOptions);

    if (matchingVariant) {
      // Store the selected variant for this product
      this.selectedVariants[productId] = matchingVariant.id;
      
      this.updateVariantDisplay(bundleOption, matchingVariant);
      
      const radioInput = bundleOption.querySelector('input[type="radio"]');
      if (radioInput) {
        if (!radioInput.checked) {
          radioInput.checked = true;
        }
        radioInput.dataset.variantId = matchingVariant.id;
        radioInput.value = matchingVariant.id;
        radioInput.dataset.price = matchingVariant.price;
        
        this.onProductChange(bundleOption);
      }
    }
  }

  getSelectedOptions(variantSelector) {
    const options = [];
    
    // Get from active swatches/buttons
    variantSelector.querySelectorAll('.bundle-swatch.is-active, .bundle-variant-btn.is-active').forEach(btn => {
      const index = parseInt(btn.dataset.optionIndex);
      const value = btn.dataset.value;
      options[index] = value;
      console.log('Active option found:', { index, value, optionName: btn.dataset.optionName });
    });

    // Get from selects
    variantSelector.querySelectorAll('.bundle-variant-select').forEach(select => {
      const index = parseInt(select.dataset.optionIndex);
      options[index] = select.value;
    });

    console.log('All selected options:', options);
    return options;
  }

  findVariant(productId, selectedOptions) {
    const variants = this.variantData[productId];
    if (!variants) {
      console.warn('No variants found for product', productId);
      return null;
    }

    console.log('Finding variant for product', productId);
    console.log('Available variants:', variants.map(v => ({ id: v.id, title: v.title, options: v.options })));
    console.log('Looking for options:', selectedOptions);

    const found = variants.find(variant => {
      const matches = variant.options.every((opt, index) => {
        const selected = selectedOptions[index];
        const isMatch = !selected || opt === selected;
        console.log(`  Option ${index}: variant has "${opt}", selected is "${selected}", match: ${isMatch}`);
        return isMatch;
      });
      console.log(`  Variant ${variant.id} (${variant.title}): ${matches ? 'MATCH' : 'no match'}`);
      return matches;
    });

    console.log('Found variant:', found);
    return found;
  }

  updateVariantDisplay(bundleOption, variant) {
    // Update radio input data
    const radioInput = bundleOption.querySelector('input[type="radio"]');
    if (radioInput) {
      radioInput.value = variant.id;
      radioInput.dataset.variantId = variant.id;
      radioInput.dataset.price = variant.price;
    }

    // Update hidden input if this is the selected product
    if (radioInput?.checked && this.thermostaatInput) {
      this.thermostaatInput.value = variant.id;
    }

    // Update price display
    const priceEl = bundleOption.querySelector('[data-variant-price]');
    if (priceEl) {
      priceEl.textContent = '+ ' + this.formatMoney(variant.price);
    }

    // Update compare at price
    const comparePriceEl = bundleOption.querySelector('[data-variant-compare-price]');
    if (comparePriceEl) {
      if (variant.compareAtPrice && variant.compareAtPrice > variant.price) {
        comparePriceEl.textContent = this.formatMoney(variant.compareAtPrice);
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    // Update image if variant has one
    if (variant.image) {
      const imageContainer = bundleOption.querySelector('[data-option-image]');
      if (imageContainer) {
        const img = imageContainer.querySelector('img');
        if (img) {
          img.src = variant.image;
          img.srcset = '';
        }
      }
    }
  }

  updateTotal() {
    const config = this.getConfig();
    if (!config) return;

    // Get main product variant info
    const mainVariant = window.customVariantSelector?.getCurrentVariant();
    const mainPrice = mainVariant ? mainVariant.price : config.mainPrice;
    const mainTitle = mainVariant ? mainVariant.title : '';
    
    const thermostaatData = this.getSelectedThermostaat();
    const thermostaatPrice = thermostaatData ? parseInt(thermostaatData.price) : 0;
    const total = mainPrice + thermostaatPrice;

    // Update displays
    const qtyDisplay = this.selector.querySelector('[data-bundle-qty]');
    const mainPriceDisplay = this.selector.querySelector('[data-main-price]');
    const thermostaatNameDisplay = this.selector.querySelector('[data-thermostaat-name]');
    const thermostaatPriceDisplay = this.selector.querySelector('[data-thermostaat-price]');
    const thermostaatRow = this.selector.querySelector('[data-thermostaat-row]');
    const totalDisplay = this.selector.querySelector('[data-bundle-total-price]');

    // Format title: "1.5 m2" → "1,5 m²" and remove duplicate m²
    let formattedTitle = mainTitle
      .replace(/\./g, ',')
      .replace(/m2/gi, 'm²');
    
    // Remove duplicate m² (e.g., "30 m² m²" → "30 m²")
    formattedTitle = formattedTitle.replace(/m²\s*m²/gi, 'm²');

    if (qtyDisplay) qtyDisplay.textContent = formattedTitle || '1 m²';
    if (mainPriceDisplay) mainPriceDisplay.textContent = this.formatMoney(mainPrice);
    
    if (thermostaatData && thermostaatData.variantId) {
      // Build name with variant info
      let displayName = thermostaatData.title;
      const selectedOption = this.selector.querySelector('input[name="bundle-thermostaat"]:checked');
      if (selectedOption) {
        const bundleOption = selectedOption.closest('[data-bundle-option]');
        const variantSelector = bundleOption?.querySelector('[data-variant-selector]');
        if (variantSelector) {
          const activeSwatches = variantSelector.querySelectorAll('.bundle-swatch.is-active, .bundle-variant-btn.is-active');
          if (activeSwatches.length > 0) {
            const variantParts = [];
            activeSwatches.forEach(s => variantParts.push(s.dataset.value));
            displayName += ' - ' + variantParts.join(' / ');
          }
        }
      }
      
      if (thermostaatNameDisplay) thermostaatNameDisplay.textContent = displayName;
      if (thermostaatPriceDisplay) thermostaatPriceDisplay.textContent = '+ ' + this.formatMoney(thermostaatPrice);
      if (thermostaatRow) thermostaatRow.style.display = '';
    } else {
      if (thermostaatRow) thermostaatRow.style.display = 'none';
    }
    
    if (totalDisplay) totalDisplay.textContent = this.formatMoney(total);
  }

  getSelectedThermostaat() {
    const selected = this.selector.querySelector('input[name="bundle-thermostaat"]:checked');
    if (!selected) return null;
    
    const productId = selected.dataset.productId;
    
    // First check our tracked selected variants
    const trackedVariantId = this.selectedVariants[productId];
    
    // Use tracked variant if available, otherwise use radio's data attribute
    const variantId = trackedVariantId || selected.dataset.variantId;
    
    if (!variantId) return null;
    
    // Get the price from variant data if we have it
    let price = selected.dataset.price;
    if (trackedVariantId && this.variantData[productId]) {
      const variant = this.variantData[productId].find(v => v.id === trackedVariantId);
      if (variant) {
        price = variant.price;
      }
    }
    
    console.log('getSelectedThermostaat:', {
      productId,
      trackedVariantId,
      radioVariantId: selected.dataset.variantId,
      finalVariantId: variantId,
      price
    });
    
    return {
      variantId: variantId,
      price: price,
      title: selected.dataset.title
    };
  }

  getQuantity() {
    if (this.quantityInput) {
      return parseInt(this.quantityInput.value) || 1;
    }
    return 1;
  }

  getConfig() {
    const productInfo = document.querySelector('product-info');
    if (productInfo && window.bundleSelectorConfig) {
      const productId = productInfo.dataset.productId;
      return window.bundleSelectorConfig[productId];
    }
    return Object.values(window.bundleSelectorConfig || {})[0];
  }

  interceptFormSubmit() {
    // Find the product form
    this.productForm = document.querySelector('form[action*="/cart/add"]') || 
                       document.querySelector('product-form form') ||
                       document.querySelector('.product-form form');
    
    if (!this.productForm) {
      console.warn('Bundle selector: No product form found');
      return;
    }

    // Store reference to this for use in handlers
    const self = this;

    // For Dawn theme's product-form web component
    const productFormComponent = document.querySelector('product-form');
    if (productFormComponent) {
      // Completely override the submit handler
      productFormComponent.onSubmitHandler = async function(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        evt.stopImmediatePropagation();
        await self.handleBundleSubmit(evt);
        return false;
      };
    }
    
    // Add direct form listener with capture
    this.productForm.addEventListener('submit', async (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      evt.stopImmediatePropagation();
      await this.handleBundleSubmit(evt);
      return false;
    }, true);

    // Also intercept click on submit button directly
    const submitButtons = document.querySelectorAll('button[type="submit"][name="add"], .product-form__submit');
    submitButtons.forEach(btn => {
      btn.addEventListener('click', async (evt) => {
        const bundleSel = document.querySelector('[data-bundle-selector]');
        if (bundleSel && bundleSel.style.display !== 'none') {
          evt.preventDefault();
          evt.stopPropagation();
          evt.stopImmediatePropagation();
          await self.handleBundleSubmit(evt);
          return false;
        }
      }, true);
    });

    console.log('Bundle selector: Form interception set up');
  }

  async handleBundleSubmit(evt) {
    // Prevent double submissions
    if (this.isSubmitting) {
      console.log('Bundle selector: Already submitting, ignoring');
      return;
    }
    this.isSubmitting = true;

    // Reset any quantity inputs to 1
    const quantityInputs = document.querySelectorAll('input[name="quantity"], .quantity__input');
    quantityInputs.forEach(input => {
      input.value = '1';
    });

    const thermostaatData = this.getSelectedThermostaat();

    // Get main variant from the custom variant selector or form
    const mainVariant = window.customVariantSelector?.getCurrentVariant();
    const mainVariantInput = document.querySelector('input[name="id"]');
    const mainVariantId = mainVariant?.id || (mainVariantInput ? parseInt(mainVariantInput.value) : null);
    
    if (!mainVariantId) {
      console.error('Bundle selector: No main variant ID found');
      alert('Er ging iets mis. Ververs de pagina en probeer het opnieuw.');
      this.isSubmitting = false;
      return;
    }

    // Build items array - ALWAYS quantity 1
    const items = [];

    // Main product (vloerverwarming)
    items.push({
      id: parseInt(mainVariantId),
      quantity: 1
    });

    // Thermostaat (if selected and has valid ID)
    if (thermostaatData && thermostaatData.variantId && thermostaatData.variantId !== '') {
      items.push({
        id: parseInt(thermostaatData.variantId),
        quantity: 1
      });
    }

    console.log('Bundle selector: Adding items to cart:', JSON.stringify(items, null, 2));

    // Show loading state
    const submitButton = document.querySelector('button[type="submit"][name="add"], .product-form__submit');
    
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('loading');
      submitButton.setAttribute('aria-busy', 'true');
    }

    try {
      // Get section IDs for cart drawer - this is how Dawn does it
      const cartDrawer = document.querySelector('cart-drawer');
      let sectionsToFetch = 'cart-drawer,cart-icon-bubble';
      
      // Build the request body WITH sections - Dawn's method
      const body = {
        items: items,
        sections: sectionsToFetch,
        sections_url: window.location.pathname
      };

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || 'Failed to add to cart');
      }

      console.log('Bundle selector: Successfully added to cart:', data);
      console.log('Bundle selector: Sections received:', data.sections);

      // Update cart drawer with the sections returned from the API
      if (data.sections) {
        this.updateCartSections(data.sections);
      } else {
        console.warn('Bundle selector: No sections in response, fetching manually');
        // Fallback: fetch sections manually
        await this.fetchAndUpdateSections();
      }

      // Re-enable button
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.classList.remove('loading');
        submitButton.setAttribute('aria-busy', 'false');
      }

      // Small delay to ensure DOM is updated before opening drawer
      await new Promise(resolve => setTimeout(resolve, 50));

      // Open cart drawer
      this.openCartDrawer();

    } catch (error) {
      console.error('Bundle add to cart error:', error);
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.classList.remove('loading');
        submitButton.setAttribute('aria-busy', 'false');
      }
      alert('Er ging iets mis bij het toevoegen aan de winkelwagen: ' + error.message);
    } finally {
      this.isSubmitting = false;
    }
  }

  updateCartSections(sections) {
    console.log('Bundle selector: Updating cart sections');
    
    const cartDrawer = document.querySelector('cart-drawer');
    
    // Update cart drawer - replace entire content
    if (sections['cart-drawer'] && cartDrawer) {
      console.log('Bundle selector: Updating cart-drawer');
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
      
      // Get the new cart-drawer element from parsed HTML
      const newCartDrawer = doc.querySelector('cart-drawer');
      
      if (newCartDrawer) {
        // Copy all children from new to current
        while (cartDrawer.firstChild) {
          cartDrawer.removeChild(cartDrawer.firstChild);
        }
        
        while (newCartDrawer.firstChild) {
          cartDrawer.appendChild(newCartDrawer.firstChild);
        }
        
        // Copy attributes
        Array.from(newCartDrawer.attributes).forEach(attr => {
          if (attr.name !== 'open') { // Don't copy open state
            cartDrawer.setAttribute(attr.name, attr.value);
          }
        });
        
        console.log('Bundle selector: Cart drawer content replaced');
      }
    }

    // Update cart icon bubble
    if (sections['cart-icon-bubble']) {
      console.log('Bundle selector: Updating cart-icon-bubble');
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble = doc.getElementById('cart-icon-bubble');
        if (newBubble) {
          bubble.innerHTML = newBubble.innerHTML;
        }
      }
    }
  }

  async fetchAndUpdateSections() {
    try {
      const response = await fetch('/?sections=cart-drawer,cart-icon-bubble');
      const sections = await response.json();
      this.updateCartSections(sections);
    } catch (error) {
      console.error('Bundle selector: Failed to fetch sections:', error);
    }
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      window.location.href = '/cart';
      return;
    }

    // Use Dawn's native open method
    if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else {
      // Fallback
      cartDrawer.setAttribute('open', '');
      cartDrawer.classList.add('active');
      document.body.classList.add('overflow-hidden');
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update all cart count elements
      document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"], .cart-count').forEach(el => {
        el.textContent = cart.item_count;
      });
      
      // Show bubble if hidden
      document.querySelectorAll('.cart-count-bubble').forEach(el => {
        el.classList.remove('hidden');
      });

    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }


  formatMoney(cents) {
    const config = this.getConfig();
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    const amountWithDot = (cents / 100).toFixed(2);
    
    if (config && config.moneyFormat) {
      return config.moneyFormat
        .replace('{{amount}}', amount)
        .replace('{{amount_with_comma_separator}}', amount)
        .replace('{{amount_no_decimals}}', Math.round(cents / 100))
        .replace('{{amount_with_apostrophe_separator}}', amountWithDot)
        .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
    }
    
    return '€' + amount;
  }
}

// Initialize when DOM is ready
function initBundleSelector() {
  const selector = document.querySelector('[data-bundle-selector]');
  if (selector && !selector.dataset.initialized) {
    selector.dataset.initialized = 'true';
    window.bundleSelectorInstance = new BundleSelector(selector);
  }
}

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBundleSelector);
} else {
  initBundleSelector();
}

// Also run on Shopify section load (for theme editor)
document.addEventListener('shopify:section:load', initBundleSelector);